// Tasks tile (companion) — the user's task list on the phone, same shared Neon tables as
// the Outlook add-in, via the Pad-A cookie bridge (/api/tasks same-origin).
// v1 scope: list (grouped by company, desktop sort) · status chips · search · quick status
// toggles · one full-screen create/edit form · delete with a styled red confirm.
// No documents/logbook/people-picker on the phone (people shown read-only).
import { toast } from "./app";
import { confirmDiscard } from "./guard";
import { loadSnap, saveSnap, skeletonHtml } from "./cache";
import { makeDropdown } from "./dropdown";

const $ = <T extends Element = HTMLElement>(sel: string): T | null => document.querySelector<T>(sel);

type Status = "open" | "doing" | "done";
type Person = { contact_id: number | string; name?: string | null; email?: string | null };
type Task = {
  id: number | string; // Neon BIGINT — compare as strings
  title: string;
  description?: string | null;
  priority: number; // 0 none · 1 low · 2 med · 3 high
  status: Status;
  due_date?: string | null; // YYYY-MM-DD
  tags: string[];
  company_id?: number | string | null;
  company_name?: string | null;
  company_colour?: string | null;
  type_id?: number | string | null;
  type_name?: string | null;
  source_subject?: string | null;
  created_at: string;
  people: Person[];
  doc_count: number;
};
type Company = { id: number | string; name: string; colour?: string | null; sort_order: number };
type TType = { id: number | string; name: string; sort_order: number };

let tasks: Task[] = [];
let companies: Company[] = [];
let types: TType[] = [];
let loaded = false;
let statusFilter: "all" | Status = "all";
let showDone = false;

// editor state
let editId: string | null = null; // null = new task
let edit = { status: "open" as Status, priority: 0 };
// C360 "+ Task" (spec §8a, phone port): a new task pre-scoped to a contact. One-shot — consumed
// by the next save, cleared on close, shown as a visible People chip in the editor context.
let seedContact: { contact_id: number; name: string } | null = null;
let ddCompany: { getValue: () => string } | null = null;
let ddType: { getValue: () => string } | null = null;
let confirmDelId: string | null = null;

const esc = (s: unknown): string => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));
const PRIO_LABEL = ["None", "Low", "Medium", "High"];

// ── due dates (ported from the add-in) ──
type DueBucket = "overdue" | "today" | "week" | "later" | "none";
const DUE_ORDER: Record<DueBucket, number> = { overdue: 0, today: 1, week: 2, later: 3, none: 4 };
function dueInfo(due?: string | null): { bucket: DueBucket; label: string; cls: string } {
  if (!due) return { bucket: "none", label: "", cls: "" };
  const sod = (d: Date | string): Date => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
  const today = sod(new Date()), d = sod(due);
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  const full = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  if (diff < 0) return { bucket: "overdue", cls: "overdue", label: diff === -1 ? "Yesterday" : full };
  if (diff === 0) return { bucket: "today", cls: "soon", label: "Today" };
  if (diff <= 7) return { bucket: "week", cls: diff === 1 ? "soon" : "", label: diff === 1 ? "Tomorrow" : full };
  return { bucket: "later", cls: "", label: full };
}

// ── data ──
// Stale-while-revalidate: paint the last-known list (or a skeleton on a true first run)
// immediately, then fetch and re-render. The screen is never blanked while loading.
export async function loadTasks(force = false): Promise<void> {
  if (loaded && !force) { renderList(); return; }
  let painted = loaded; // force-refresh keeps the current list painted
  if (!loaded) {
    const snap = loadSnap<{ tasks: Task[]; companies: Company[]; types: TType[] }>("tasks");
    if (snap) { tasks = snap.tasks || []; companies = snap.companies || []; types = snap.types || []; painted = true; renderList(); }
    else { const list = $("#tkList"); if (list) list.innerHTML = skeletonHtml(); }
  }
  try {
    const r = await fetch("/api/tasks", { credentials: "include" });
    if (r.status === 401) { renderEmpty("This phone isn't linked. Reconnect from Outlook to load your tasks."); return; }
    if (r.status === 402) { renderEmpty("Tasks are part of the subscription — manage it in the Outlook add-in."); return; }
    if (!r.ok) throw new Error(String(r.status));
    const j = (await r.json()) as { tasks?: Task[]; companies?: Company[]; types?: TType[] };
    tasks = j.tasks || []; companies = j.companies || []; types = j.types || [];
    loaded = true;
    saveSnap("tasks", { tasks, companies, types });
    renderList();
  } catch {
    if (!painted) renderEmpty("Couldn't load your tasks. Check your connection and try again.");
    // painted (snapshot or current list) → keep it; the next open revalidates again
  }
}

function renderEmpty(msg: string): void {
  const list = $("#tkList"), empty = $("#tkEmpty");
  if (list) list.innerHTML = "";
  if (empty) { empty.textContent = msg; empty.style.display = "block"; }
  const count = $("#topCount"); if (count) { count.textContent = ""; count.hidden = true; }
}

// ── sort/group (ported from the add-in: Doing first → type order → prio ↓ → due ↑ → newest) ──
const typeRank = (t: Task): number => (t.type_id == null ? Infinity : (types.find((x) => String(x.id) === String(t.type_id))?.sort_order ?? 0));
const compRank = (t: Task): number => (t.company_id == null ? Infinity : (companies.find((c) => String(c.id) === String(t.company_id))?.sort_order ?? 0));
function cmp(a: Task, b: Task): number {
  const ad = a.status === "doing" ? 0 : 1, bd = b.status === "doing" ? 0 : 1;
  if (ad !== bd) return ad - bd;
  const at = typeRank(a), bt = typeRank(b);
  if (at !== bt) return at - bt;
  if (b.priority !== a.priority) return b.priority - a.priority;
  const ar = DUE_ORDER[dueInfo(a.due_date).bucket], br = DUE_ORDER[dueInfo(b.due_date).bucket];
  if (ar !== br) return ar - br;
  if (a.due_date && b.due_date && a.due_date !== b.due_date) return a.due_date < b.due_date ? -1 : 1;
  return a.created_at < b.created_at ? 1 : -1;
}

const pips = (p: number): string => `<span class="tk-pips p${p}"><i></i><i></i><i></i></span>`;

function rowHtml(t: Task): string {
  const done = t.status === "done";
  const i = dueInfo(t.due_date);
  const chips =
    (t.priority > 0 ? pips(t.priority) : "") +
    (t.due_date && !done ? `<span class="tk-due ${i.cls}">${esc(i.label)}</span>` : "") +
    (t.status === "doing" ? `<span class="tk-tag doing">Doing</span>` : "") +
    (t.type_name ? `<span class="tk-tag">${esc(t.type_name)}</span>` : "") +
    (t.doc_count > 0 ? `<span class="tk-n">📎${t.doc_count}</span>` : "") +
    (t.people.length > 0 ? `<span class="tk-n">👤${t.people.length}</span>` : "");
  const right = done
    ? `<button class="tk-del" data-del="${t.id}" title="Delete task"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg></button>`
    : `<button class="tk-doneb" data-done="${t.id}" title="Mark as done"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg></button>`;
  return (
    `<div class="tk-row${done ? " done" : ""}">` +
    `<button class="tk-check${t.status === "doing" ? " doing" : ""}" data-tg="${t.id}" title="${done ? "Reopen" : t.status === "doing" ? "Back to open" : "Mark as in progress"}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg></button>` +
    `<button class="tk-main" data-open="${t.id}"><span class="tk-title">${esc(t.title)}</span>` +
    (chips ? `<span class="tk-chips">${chips}</span>` : "") +
    `</button>${right}</div>`
  );
}

function renderList(): void {
  const term = ($("#tkSearch") as HTMLInputElement | null)?.value.trim().toLowerCase() || "";
  // Active-screen gate: the Home-boot prefetch renders in the background (see contacts.ts).
  const count = $("#topCount");
  const openCount = tasks.filter((t) => t.status !== "done").length;
  if (count && $("#screen-tasks")?.classList.contains("active")) { count.textContent = tasks.length ? String(openCount) : ""; count.hidden = !tasks.length; }

  document.querySelectorAll<HTMLElement>("#tkChips [data-st]").forEach((b) => b.classList.toggle("on", b.getAttribute("data-st") === statusFilter));

  const passes = (t: Task): boolean => {
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    if (term) {
      const hay = `${t.title} ${t.description || ""} ${t.company_name || ""} ${t.type_name || ""} ${t.people.map((p) => p.name || "").join(" ")} ${(t.tags || []).join(" ")}`.toLowerCase();
      if (!hay.includes(term)) return false;
    }
    return true;
  };

  const active = tasks.filter((t) => t.status !== "done" && passes(t));
  const done = statusFilter === "done" ? tasks.filter(passes) : statusFilter === "all" ? tasks.filter((t) => t.status === "done" && passes(t)) : [];

  const list = $("#tkList"), empty = $("#tkEmpty");
  if (!list) return;
  if (!active.length && !done.length) {
    list.innerHTML = "";
    if (empty) { empty.textContent = tasks.length ? "No tasks match." : "No tasks yet. Tap New to add one."; empty.style.display = "block"; }
    return;
  }
  if (empty) empty.style.display = "none";

  // group the active tasks by company (sort_order; "None" last)
  type G = { key: string; label: string; dot: string | null; order: number; items: Task[] };
  const map = new Map<string, G>();
  for (const t of active) {
    const key = String(t.company_id ?? "none");
    let g = map.get(key);
    if (!g) {
      g = { key, label: t.company_name || "None", dot: t.company_colour || null, order: t.company_id == null ? 9e9 : compRank(t), items: [] };
      map.set(key, g);
    }
    g.items.push(t);
  }
  const groups = [...map.values()].sort((a, b) => (a.order - b.order) || a.label.localeCompare(b.label));
  groups.forEach((g) => g.items.sort(cmp));
  done.sort(cmp);

  let html = groups
    .map((g) =>
      `<div class="tk-group"><div class="tk-group-hd">` +
      (g.dot ? `<span class="tk-dot" style="background:${esc(g.dot)}"></span>` : "") +
      `<span>${esc(g.label)}</span><span class="tk-gn">${g.items.length}</span></div>` +
      g.items.map(rowHtml).join("") +
      `</div>`,
    )
    .join("");

  if (done.length && statusFilter !== "done") {
    html +=
      `<div class="tk-group"><button class="tk-group-hd tk-done-toggle" id="tkDoneToggle">` +
      `<svg class="${showDone ? "open" : ""}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>` +
      `<span>Done</span><span class="tk-gn">${done.length}</span></button>` +
      (showDone ? done.map(rowHtml).join("") : "") +
      `</div>`;
  } else if (done.length) {
    html += `<div class="tk-group">${done.map(rowHtml).join("")}</div>`;
  }
  list.innerHTML = html;
}

// ── quick status changes (optimistic) ──
async function patchTask(id: string, body: Record<string, unknown>): Promise<boolean> {
  try {
    const r = await fetch(`/api/tasks/${id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    return r.ok;
  } catch { return false; }
}
function setStatusLocal(id: string, ns: Status): void {
  tasks = tasks.map((t) => (String(t.id) === id ? { ...t, status: ns } : t));
  renderList();
}
async function quickStatus(id: string, ns: Status): Promise<void> {
  const prev = tasks.find((t) => String(t.id) === id)?.status;
  if (!prev || prev === ns) return;
  setStatusLocal(id, ns); // optimistic
  if (!(await patchTask(id, { status: ns }))) { setStatusLocal(id, prev); toast("Couldn't update — try again"); }
}

// ── editor (create + edit on one full-screen form) ──
/** Home-search result tap → straight into the task editor (after loadTasks' snapshot paint). */
export function openTaskById(id: string): void {
  openEditor(id);
}
/** C360 "+ Task": open the create editor with the contact pre-attached (saved via contact_id). */
export function openTaskFor(c: { contact_id: number; name?: string | null }): void {
  seedContact = { contact_id: c.contact_id, name: (c.name || "").trim() };
  openEditor(null);
}
function openEditor(id: string | null): void {
  editId = id;
  if (id) seedContact = null; // editing an existing task never carries a create-seed
  const t = id ? tasks.find((x) => String(x.id) === id) : null;
  edit = { status: t?.status || "open", priority: t?.priority ?? 0 };

  const title = $("#tkEdTitle"); if (title) title.textContent = t ? "Task" : "New task";
  const del = $("#tkEdDelete") as HTMLElement | null; if (del) del.hidden = !t;
  const mail = $("#tkEdMail") as HTMLElement | null; if (mail) mail.hidden = !t; // existing tasks only

  ($("#tkfTitle") as HTMLInputElement | null)!.value = t?.title || "";
  ($("#tkfDesc") as HTMLTextAreaElement | null)!.value = t?.description || "";
  ($("#tkfDue") as HTMLInputElement | null)!.value = t?.due_date || "";
  ($("#tkfTags") as HTMLInputElement | null)!.value = (t?.tags || []).join(", ");

  syncSeg(); syncPrio();

  // company + type — the custom .rp-dd dropdowns (HARD RULE: never a native select)
  const coHost = $("#tkfCompany"), tyHost = $("#tkfType");
  if (coHost) {
    coHost.innerHTML = "";
    const dd = makeDropdown({
      placeholder: "— none —",
      value: t?.company_id != null ? String(t.company_id) : "",
      searchable: companies.length > 5,
      options: [{ value: "", label: "— none —" }, ...companies.map((c) => ({ value: String(c.id), label: c.name }))],
    });
    coHost.appendChild(dd.el); ddCompany = dd;
  }
  if (tyHost) {
    tyHost.innerHTML = "";
    const dd = makeDropdown({
      placeholder: "— none —",
      value: t?.type_id != null ? String(t.type_id) : "",
      searchable: types.length > 5,
      options: [{ value: "", label: "— none —" }, ...types.map((x) => ({ value: String(x.id), label: x.name }))],
    });
    tyHost.appendChild(dd.el); ddType = dd;
  }

  // read-only context: linked people + created-from-email subject
  const ctx = $("#tkEdCtx");
  if (ctx) {
    const ppl = (t?.people || []).map((p) => `<span class="tk-tag">${esc(p.name || p.email || "—")}</span>`).join("")
      || (!t && seedContact ? `<span class="tk-tag">${esc(seedContact.name || "Linked contact")}</span>` : "");
    ctx.innerHTML =
      (ppl ? `<div class="tk-ctx-row"><span class="tk-ctx-k">People</span><span class="tk-ctx-v">${ppl}</span></div>` : "") +
      (t?.source_subject ? `<div class="tk-ctx-row"><span class="tk-ctx-k">From email</span><span class="tk-ctx-v">${esc(t.source_subject)}</span></div>` : "");
    ctx.style.display = ctx.innerHTML ? "block" : "none";
  }

  $("#tkEditor")?.classList.add("open");
}
function closeEditor(): void { $("#tkEditor")?.classList.remove("open"); editId = null; seedContact = null; }

function syncSeg(): void {
  document.querySelectorAll<HTMLElement>("#tkfStatus [data-s]").forEach((b) => b.classList.toggle("on", b.getAttribute("data-s") === edit.status));
}
function syncPrio(): void {
  document.querySelectorAll<HTMLElement>("#tkfPrio [data-p]").forEach((b) => b.classList.toggle("on", Number(b.getAttribute("data-p")) === edit.priority));
}

function collectFields(title?: string) {
  return {
    title: title ?? (($("#tkfTitle") as HTMLInputElement | null)?.value.trim() || ""),
    description: ($("#tkfDesc") as HTMLTextAreaElement | null)?.value.trim() || "",
    company_id: ddCompany?.getValue() || null,
    type_id: ddType?.getValue() || null,
    priority: edit.priority,
    status: edit.status,
    due_date: ($("#tkfDue") as HTMLInputElement | null)?.value || "",
    tags: (($("#tkfTags") as HTMLInputElement | null)?.value || "").split(",").map((s) => s.trim()).filter(Boolean),
  };
}

// SAVE-CANON C2/C4: back on an EXISTING task auto-saves the diff (the pane's canon — previously
// typed edits were wiped); back on a dirty NEW task asks the shared Discard confirm.
async function flushCloseTasks(): Promise<void> {
  const f = collectFields();
  if (editId) {
    const t = tasks.find((x) => String(x.id) === editId);
    const patch: Record<string, unknown> = {};
    if (t) {
      if (f.title && f.title !== t.title) patch.title = f.title;
      if (f.description !== (t.description || "")) patch.description = f.description;
      if (String(f.company_id ?? "") !== String(t.company_id ?? "")) patch.company_id = f.company_id;
      if (String(f.type_id ?? "") !== String(t.type_id ?? "")) patch.type_id = f.type_id;
      if (f.priority !== t.priority) patch.priority = f.priority;
      if (f.status !== t.status) patch.status = f.status;
      if ((f.due_date || "") !== (t.due_date || "")) patch.due_date = f.due_date;
      if (f.tags.join("¦") !== (t.tags || []).join("¦")) patch.tags = f.tags;
    }
    if (Object.keys(patch).length) {
      const ok = await patchTask(editId, patch);
      if (!ok) { toast("Couldn't save — try again"); return; } // stay open, nothing lost
      void loadTasks(true);
    }
  } else if (f.title || f.description || f.tags.length) {
    if (!(await confirmDiscard("this task"))) return;
  }
  closeEditor();
}

async function saveEditor(): Promise<void> {
  const title = ($("#tkfTitle") as HTMLInputElement | null)?.value.trim() || "";
  if (!title) { toast("Give the task a title"); return; }
  const btn = $("#tkEdSave") as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = "Saving…"; }

  const fields = collectFields(title);

  let ok = false;
  if (!editId) {
    try {
      const r = await fetch("/api/tasks", {
        method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
        body: JSON.stringify({ ...fields, ...(seedContact ? { contact_id: seedContact.contact_id } : {}) }),
      });
      ok = r.ok;
    } catch { ok = false; }
  } else {
    // send only CHANGED fields (the server logs due/status changes — don't spam the logbook)
    const t = tasks.find((x) => String(x.id) === editId);
    const patch: Record<string, unknown> = {};
    if (t) {
      if (fields.title !== t.title) patch.title = fields.title;
      if (fields.description !== (t.description || "")) patch.description = fields.description;
      if (String(fields.company_id ?? "") !== String(t.company_id ?? "")) patch.company_id = fields.company_id;
      if (String(fields.type_id ?? "") !== String(t.type_id ?? "")) patch.type_id = fields.type_id;
      if (fields.priority !== t.priority) patch.priority = fields.priority;
      if (fields.status !== t.status) patch.status = fields.status;
      if ((fields.due_date || "") !== (t.due_date || "")) patch.due_date = fields.due_date;
      if (fields.tags.join("¦") !== (t.tags || []).join("¦")) patch.tags = fields.tags;
    }
    ok = Object.keys(patch).length ? await patchTask(editId, patch) : true;
  }

  if (btn) { btn.disabled = false; btn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg> Save task`; }
  if (!ok) { toast("Couldn't save — try again"); return; }
  closeEditor();
  toast(editId ? "Task updated" : "Task added");
  await loadTasks(true);
}

// ── delete (styled red confirm — house rule for destructive actions) ──
function askDelete(id: string): void {
  confirmDelId = id;
  const t = tasks.find((x) => String(x.id) === id);
  const b = $("#tkCfBody");
  if (b) b.innerHTML = `<b>${esc(t?.title || "This task")}</b> and its logbook and links will be permanently removed. This can't be undone.`;
  $("#tkConfirm")?.classList.add("open");
}
function closeConfirm(): void { $("#tkConfirm")?.classList.remove("open"); confirmDelId = null; }
async function doDelete(): Promise<void> {
  const id = confirmDelId;
  closeConfirm();
  if (!id) return;
  try {
    const r = await fetch(`/api/tasks/${id}`, { method: "DELETE", credentials: "include" });
    if (!r.ok) throw new Error(String(r.status));
    closeEditor();
    toast("Task deleted");
    await loadTasks(true);
  } catch { toast("Couldn't delete — try again"); }
}

// "Email this task to me" — the server (S2S to the Outlook engine) mails the branded task card,
// with its stored documents attached, to your own address via Resend.
async function mailTask(): Promise<void> {
  if (!editId) return;
  const btn = $("#tkEdMail") as HTMLButtonElement | null;
  const label = btn?.innerHTML;
  if (btn) { btn.disabled = true; btn.textContent = "Sending…"; }
  try {
    const r = await fetch("/api/tasks/mail", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify({ task_id: Number(editId) }),
    });
    const j = (await r.json().catch(() => ({}))) as { ok?: boolean; to?: string; attached?: number; error?: string };
    if (!r.ok || !j.ok) { toast(j.error === "no_user_email" ? "No address on your account" : "Couldn't send — try again"); return; }
    toast(`Sent to ${j.to || "you"}${j.attached ? ` · ${j.attached} file(s) attached` : ""}`);
  } catch { toast("Couldn't send — check your connection"); }
  finally { if (btn) { btn.disabled = false; if (label) btn.innerHTML = label; } }
}

// ── wiring ──
export function initTasks(): void {
  const search = $("#tkSearch") as HTMLInputElement | null;
  const onSearch = (): void => { renderList(); const cl = $("#tkClear"); if (cl) cl.hidden = !search?.value; };
  search?.addEventListener("input", onSearch);
  $("#tkClear")?.addEventListener("click", () => { if (search) search.value = ""; onSearch(); search?.focus(); });
  $("#tkNew")?.addEventListener("click", () => openEditor(null));

  $("#tkChips")?.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-st]");
    if (!b) return;
    statusFilter = (b.getAttribute("data-st") as typeof statusFilter) || "all";
    renderList();
  });

  $("#tkList")?.addEventListener("click", (e) => {
    const el = e.target as HTMLElement;
    const tg = el.closest<HTMLElement>("[data-tg]");
    if (tg) {
      const id = tg.getAttribute("data-tg")!;
      const t = tasks.find((x) => String(x.id) === id);
      if (t) void quickStatus(id, t.status === "done" ? "open" : t.status === "open" ? "doing" : "open");
      return;
    }
    const dn = el.closest<HTMLElement>("[data-done]");
    if (dn) { void quickStatus(dn.getAttribute("data-done")!, "done"); return; }
    const del = el.closest<HTMLElement>("[data-del]");
    if (del) { askDelete(del.getAttribute("data-del")!); return; }
    if (el.closest("#tkDoneToggle")) { showDone = !showDone; renderList(); return; }
    const open = el.closest<HTMLElement>("[data-open]");
    if (open) openEditor(open.getAttribute("data-open")!);
  });

  // editor
  $("#tkEdBack")?.addEventListener("click", () => void flushCloseTasks());
  $("#tkEdSave")?.addEventListener("click", () => void saveEditor());
  $("#tkEdMail")?.addEventListener("click", () => void mailTask());
  $("#tkEdDelete")?.addEventListener("click", () => { if (editId) askDelete(editId); });
  $("#tkfStatus")?.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-s]");
    if (b) { edit.status = (b.getAttribute("data-s") as Status) || "open"; syncSeg(); }
  });
  $("#tkfPrio")?.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-p]");
    if (b) { edit.priority = Number(b.getAttribute("data-p")) || 0; syncPrio(); }
  });

  // confirm
  $("#tkCfCancel")?.addEventListener("click", closeConfirm);
  $("#tkCfScrim")?.addEventListener("click", closeConfirm);
  $("#tkCfDelete")?.addEventListener("click", () => void doDelete());
}
